import React from "react";
import { Shield, AlertTriangle, FileCheck } from "lucide-react";

interface WizardData {
  [key: string]: any;
  conflictCheckStatus: string;
  conflictCheckNotes: string;
  amlStatus: string;
  amlProvider: string;
  amlRiskScore: string;
  onboardingNotes: string;
}

interface ComplianceStepProps {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
  errors: Record<string, string>;
}

const CONFLICT_OPTIONS = [
  { value: "not_started", label: "Not Started", description: "Conflict check has not been performed yet", color: "slate" },
  { value: "passed", label: "Passed", description: "No conflicts found  -  cleared to proceed", color: "emerald" },
  { value: "waived", label: "Waived", description: "Conflict exists but has been waived", color: "amber" },
];

const AML_OPTIONS = [
  { value: "not_required", label: "Not Required", description: "AML/KYC verification not needed for this client" },
  { value: "pending", label: "Pending", description: "Verification is in progress" },
  { value: "verified", label: "Verified", description: "Client identity has been verified" },
];

const ComplianceStep: React.FC<ComplianceStepProps> = ({ data, onChange, errors }) => {
  return (
    <div className="space-y-6">
      {/* Conflict Check */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-4.5 w-4.5 text-slate-600" />
          <h3 className="text-sm font-semibold text-slate-800">Conflict Check</h3>
        </div>
        <div className="space-y-2">
          {CONFLICT_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                data.conflictCheckStatus === opt.value
                  ? "border-brand-accent bg-brand-light/50 ring-1 ring-brand-light"
                  : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
              }`}
            >
              <input
                type="radio"
                name="conflictCheck"
                value={opt.value}
                checked={data.conflictCheckStatus === opt.value}
                onChange={() => onChange({ conflictCheckStatus: opt.value })}
                className="mt-0.5 h-4 w-4 text-emerald-600 border-slate-300 focus:ring-emerald-500"
              />
              <div>
                <span className="text-sm font-medium text-slate-800">{opt.label}</span>
                <p className="text-xs text-slate-500 mt-0.5">{opt.description}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Notes for passed/waived */}
        {(data.conflictCheckStatus === "passed" || data.conflictCheckStatus === "waived") && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {data.conflictCheckStatus === "waived" ? "Waiver Reason" : "Notes"}
              {data.conflictCheckStatus === "waived" && <span className="text-red-500"> *</span>}
            </label>
            <textarea
              value={data.conflictCheckNotes}
              onChange={(e) => onChange({ conflictCheckNotes: e.target.value })}
              rows={2}
              className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 resize-none ${
                errors.conflictCheckNotes ? "border-red-300" : "border-slate-200"
              }`}
              placeholder={
                data.conflictCheckStatus === "waived"
                  ? "Reason for waiving conflict check..."
                  : "Notes about the conflict check..."
              }
            />
            {errors.conflictCheckNotes && <p className="text-xs text-red-500 mt-1">{errors.conflictCheckNotes}</p>}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-slate-100" />

      {/* AML / KYC */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <FileCheck className="h-4.5 w-4.5 text-slate-600" />
          <h3 className="text-sm font-semibold text-slate-800">AML / Know Your Client</h3>
        </div>
        <div className="space-y-2">
          {AML_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                data.amlStatus === opt.value
                  ? "border-brand-accent bg-brand-light/50 ring-1 ring-brand-light"
                  : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
              }`}
            >
              <input
                type="radio"
                name="amlStatus"
                value={opt.value}
                checked={data.amlStatus === opt.value}
                onChange={() => onChange({ amlStatus: opt.value })}
                className="mt-0.5 h-4 w-4 text-emerald-600 border-slate-300 focus:ring-emerald-500"
              />
              <div>
                <span className="text-sm font-medium text-slate-800">{opt.label}</span>
                <p className="text-xs text-slate-500 mt-0.5">{opt.description}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Verified details */}
        {data.amlStatus === "verified" && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Provider</label>
              <input
                type="text"
                value={data.amlProvider}
                onChange={(e) => onChange({ amlProvider: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
                placeholder="e.g. Jumio, Onfido"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Risk Score</label>
              <input
                type="number"
                value={data.amlRiskScore}
                onChange={(e) => onChange({ amlRiskScore: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
                placeholder="0-100"
                min={0}
                max={100}
              />
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-slate-100" />

      {/* Onboarding Notes */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Onboarding Notes</label>
        <textarea
          value={data.onboardingNotes}
          onChange={(e) => onChange({ onboardingNotes: e.target.value })}
          rows={3}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 resize-none"
          placeholder="General notes about this client onboarding..."
        />
      </div>
    </div>
  );
};

export default ComplianceStep;
